import clsx from 'clsx'

import Icon from '../../components/Icon'
import LoadingText from '../../components/LoadingText'
import type { LoadableComponentProps } from '../../types'

type TableHeader = {
  title: string
  subtitle?: string | null
}

type TableCell = {
  text?: string | null
  icon?: string | null
}

type Column<K extends string> = {
  key: K
  header: TableHeader
  featured?: boolean
}

type TableProps = {
  columns: Column<string>[]
  rows: Array<Record<string, TableCell>>
}

interface BenefitsTableProps {
  title: string
  remainingPointsText?: string
  table: TableProps
}

export default function BenefitsTable({
  data,
  loading,
}: LoadableComponentProps<BenefitsTableProps>) {
  const { title, remainingPointsText, table = { columns: [], rows: [] } } = data || {}
  const { columns, rows } = table

  return (
    <div
      className='isolate mx-auto w-full px-5 py-10'
      style={{
        maxWidth: 'var(--layout-max-width, none)',
      }}
    >
      <h2 className='h5 mb-6'>
        {loading ? <LoadingText className='inline' textLength={15} /> : title}
      </h2>
      {remainingPointsText && (
        <p className='sm mb-6 uppercase text-darkgray'>
          {loading ? <LoadingText className='inline' textLength={10} /> : remainingPointsText}
        </p>
      )}
      <div className='radius-md w-full divide-y-2 divide-solid divide-gray/20 bg-steelblue p-2 md:p-5 lg:rounded-md'>
        <div className='w-full overflow-x-auto'>
          <table className='w-full border-collapse'>
            <thead>
              <tr className='shadow-[0_1px_0_0_rgba(117,117,117,1)]'>
                {columns.map((col, colIndex) => (
                  <th key={col.key} className='text-center align-baseline font-normal'>
                    <div
                      className={clsx(
                        'flex flex-col items-center border-l border-gray/35 px-1 py-5',
                        colIndex === 0 && 'border-none',
                        col.featured &&
                          'rounded-t-md border-x border-t-[20px] border-highlight text-highlight',
                        colIndex > 0 && columns[colIndex - 1].featured && 'border-none',
                        colIndex === 0 && '!items-start',
                      )}
                    >
                      <p className='mt-1 whitespace-nowrap leading-none'>{col.header.title}</p>
                      {col.header.subtitle && (
                        <p className='sm whitespace-nowrap leading-none text-darkgray'>
                          {col.header.subtitle}
                        </p>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className=''>
              {rows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className='shadow-[0_1px_0_0_rgba(164,164,164,0.35)] last:shadow-none'
                >
                  {columns.map((col, colIndex) => {
                    const cell = row[col.key]

                    return (
                      <td
                        key={col.key}
                        className={clsx(
                          'text-darkgray [&>div]:first:border-none [&>div]:first:text-black',
                          rowIndex % 2 === 0 && 'bg-lightgray/50',
                          rowIndex === rows.length - 1 &&
                            'first:rounded-bl-sm last:rounded-br-sm first:lg:rounded-bl-md last:lg:rounded-br-md',
                          col.featured && '!rounded-none',
                        )}
                      >
                        <div
                          className={clsx(
                            'flex min-h-[60px] flex-1 flex-col items-center justify-center border-l border-gray/35 p-2',
                            col.featured && 'border-x border-highlight text-highlight',
                            rowIndex === rows.length - 1 && col.featured && 'border-b',
                            colIndex > 0 && columns[colIndex - 1].featured && 'border-none',
                            colIndex === 0 && '!items-start',
                          )}
                        >
                          {cell?.icon && <Icon name={cell.icon} size={16} />}

                          {cell?.text && (
                            <p
                              className={clsx(
                                'sm whitespace-nowrap',
                                cell.icon && 'xs leading-none',
                              )}
                            >
                              {cell.text}
                            </p>
                          )}
                        </div>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
